import React, { useState } from "react";
import { InputMask } from "primereact/inputmask";
import { InputText } from "primereact/inputtext";
import { translate } from "../../../services/i18n";
import { Button } from "primereact/button";
import { storeSteps } from "../../../services/util";
import { api, viacep, opencep, brasilapi } from "../../../services/api";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import "../styles.scss";

// ViaCEP é comunitário e sem SLA — já confirmamos em produção um CEP real (35164-383,
// Ipatinga-MG) que ele diz não existir, mas OpenCEP e BrasilAPI resolvem corretamente. Mesma
// cadeia de fallback usada no backend (Picpay3dsSetupProcessor.ResolveBillingAddress). Cada
// provedor tem seu próprio formato de path (só o ViaCEP usa o sufixo "/json/").
const CEP_PROVIDERS = [
  { client: viacep, path: (digits) => `${digits}/json/` },
  { client: opencep, path: (digits) => `${digits}` },
  { client: brasilapi, path: (digits) => `${digits}` },
];

async function isCepResolvable(digits) {
  for (const { client, path } of CEP_PROVIDERS) {
    try {
      const response = await client.get(path(digits));
      if (response.data && response.data.cep) {
        return true;
      }
    } catch (err) {
      // tenta o próximo provedor
    }
  }
  return false;
}

export function MissingFields({ missing, setMissing, transactionData }) {
  const [data, setData] = useState({});
  const [validPhone, setValidPhone] = useState(false);
  const [validCEP, setValidCEP] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const modules = {
    phone: () => {
      const customMasks = { tw: "... ... ..." };

      function isPhoneValid(phone, country) {
        if (!phone || !country?.countryCode) return false;
        try {
          return isValidPhoneNumber("+" + phone, country.countryCode.toUpperCase());
        } catch {
          return false;
        }
      }

      return (
        <PhoneInput
          key="phone"
          isValid={(inputNumber, country) => isPhoneValid(inputNumber, country)}
          country={"br"}
          masks={customMasks}
          preferredCountries={["br", "us"]}
          placeholder={translate("page.confirmation.phone")}
          value={data.phone}
          onChange={(phone, country) => {
            setData({ ...data, phone });
            const valid = isPhoneValid(phone, country);
            setValidPhone(valid);
            setErrors((prev) => ({
              ...prev,
              phone: valid ? "" : translate("page.confirmation.error.phone"),
            }));
          }}
        />
      );
    },
    fullname: () => {
      return (
        <InputText
          key="Fullname"
          placeholder={translate("page.confirmation.fullname")}
          value={data.fullname}
          onChange={(fullname) => {
            const value = fullname.target.value;
            setData({ ...data, fullname: value });
            if (!value || value.indexOf("_") !== -1) {
              setErrors((prev) => ({
                ...prev,
                fullname: translate("page.confirmation.error.fullname"),
              }));
            } else {
              setErrors((prev) => ({ ...prev, fullname: "" }));
            }
          }}
        />
      );
    },
    cep: () => {
      return (
        <InputMask
          key="CEP"
          placeholder={translate("page.confirmation.cep")}
          value={data.cep}
          onChange={(cep) => {
            const value = cep.target.value;
            isCEPValid(value);
            setData({ ...data, cep: value });
          }}
          mask="99999-999"
        />
      );
    },
    cpf: () => {
      return (
        <InputMask
          key="CPF"
          placeholder={translate("page.confirmation.cpf")}
          value={data.cpf}
          mask="999.999.999-99"
          onChange={(cpf) => {
            const value = cpf.target.value;
            setData({ ...data, cpf: value });
            if (!value || value.indexOf("_") !== -1) {
              setErrors((prev) => ({
                ...prev,
                cpf: translate("page.confirmation.error.incompleteCpf"),
              }));
            } else if (!isCPFValid(value)) {
              setErrors((prev) => ({
                ...prev,
                cpf: translate("page.confirmation.error.invalidCpf"),
              }));
            } else {
              setErrors((prev) => ({ ...prev, cpf: "" }));
            }
          }}
        />
      );
    },
  };

  async function isCEPValid(value) {
    if (!value) {
      setValidCEP(false);
      setErrors((prev) => ({
        ...prev,
        cep: translate("page.confirmation.error.incompleteCep"),
      }));
      return;
    }

    const digits = (value.match(/\d+/g) || []).join("");

    if (digits.length < 8) {
      setValidCEP(false);
      setErrors((prev) => ({
        ...prev,
        cep: translate("page.confirmation.error.incompleteCep"),
      }));
      return;
    }

    if (digits.length === 8) {
      const isValid = await isCepResolvable(digits);
      setValidCEP(isValid);
      setErrors((prev) => ({
        ...prev,
        cep: isValid ? "" : translate("page.confirmation.error.invalidCep"),
      }));
      return;
    }

    // more than 8 digits — treat as invalid
    setValidCEP(false);
    setErrors((prev) => ({
      ...prev,
      cep: translate("page.confirmation.error.invalidCep"),
    }));
  }

  function validInput() {
    const validFunction = {
      cep: (value) => validCEP,
      cpf: (value) => {
        return value
          ? value.length > 0 && value.indexOf("_") === -1 && isCPFValid(value)
          : false;
      },
      fullname: (value) => {
        return value ? value.length > 0 && value.indexOf("_") === -1 : false;
      },
      phone: (value) => validPhone,
    };

    var invalids = missing.filter((miss) => {
      return !validFunction[miss](data[miss]);
    });

    const hasErrors = Object.values(errors || {}).some(
      (v) => v && v.length > 0,
    );

    return invalids.length === 0 && !hasErrors;
  }

  function isCPFValid(value) {
    var strCPF = value.match(/\d+/g).join("");
    var Soma;
    var Resto;

    Soma = 0;
    if (strCPF === "00000000000") return false;

    for (var i = 1; i <= 9; i++)
      Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (11 - i);
    Resto = (Soma * 10) % 11;

    if (Resto === 10 || Resto === 11) Resto = 0;
    if (Resto !== parseInt(strCPF.substring(9, 10))) return false;

    Soma = 0;
    for (i = 1; i <= 10; i++)
      Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (12 - i);
    Resto = (Soma * 10) % 11;

    if (Resto === 10 || Resto === 11) Resto = 0;
    if (Resto !== parseInt(strCPF.substring(10, 11))) return false;
    return true;
  }

  async function submitMissingFields() {
    const cleanFunction = {
      cep: (value) => value.match(/\d+/g).join(""),
      cpf: (value) => value.match(/\d+/g).join(""),
      fullname: (value) => value,
      // O react-phone-input-2 devolve o número com o código do país, mas sem o "+". Sem ele o
      // backend faz o parse com a região BR e um número estrangeiro (ex.: 12025550135) é
      // recusado. Envia em E.164.
      phone: (value) => "+" + value.match(/\d+/g).join(""),
    };

    const params = new URLSearchParams();
    params.append("operation", "updateInformation");
    params.append("username", transactionData.username);
    params.append("token", transactionData.token);
    missing.forEach((miss) => {
      params.append(miss, cleanFunction[miss](data[miss]));
    });

    setSubmitting(true);
    setSubmitError("");
    try {
      const response = await api.post("payment/personal-info", params);

      // O backend responde 200 também quando recusa o envio ({status:"fail", message}). Só
      // avança quando os dados foram de fato salvos; senão o jogador seguia para o pagamento
      // com o telefone vazio no cadastro.
      if (response.data?.status === "success") {
        setMissing({});
        return;
      }

      if (response.data?.message === "invalid phone format") {
        setValidPhone(false);
        setErrors((prev) => ({
          ...prev,
          phone: translate("page.confirmation.error.phone"),
        }));
      } else {
        setSubmitError(translate("page.confirmation.error.submitMissingFields"));
      }
    } catch (error) {
      console.error(
        translate("page.confirmation.error.submitMissingFields"),
        error,
      );
      setSubmitError(translate("page.confirmation.error.submitMissingFields"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-justify-center p-md-12">
      <div className="p-grid p-fluid p-md-4">
        {missing.map((item) => {
          return (
            <div key={item} className="p-md-12">
              {modules[item]()}
              {errors[item] && (
                <small className="p-error missing-field-error">
                  {errors[item]}
                </small>
              )}
            </div>
          );
        })}
        <div className="p-md-12">
          <Button
            label={translate("page.confirmation.submit")}
            disabled={submitting || !validInput()}
            onClick={submitMissingFields}
          />
          {submitError && (
            <small className="p-error missing-field-error">{submitError}</small>
          )}
        </div>
      </div>
      {storeSteps(3)}
    </div>
  );
}
